import React from 'react';
import {
  SpeciesDominantOverlayMeta,
  SpeciesRichnessOverlayMeta,
} from '../../lib/types';
import { LegendCard } from './LegendCard';

interface SpeciesMapLegendProps {
  meta: SpeciesDominantOverlayMeta | SpeciesRichnessOverlayMeta;
}

// matplotlib viridis sampled at 5 stops. The precompute PNG uses the same
// colormap, so this CSS gradient is a visual stand-in (not pixel-matched) for
// the raster values.
const VIRIDIS_CSS_GRADIENT =
  'linear-gradient(to right, #440154 0%, #3b528b 25%, #21918c 50%, #5ec962 75%, #fde725 100%)';

const DominantLegendBody: React.FC<{ meta: SpeciesDominantOverlayMeta }> = ({ meta }) => (
  <div className="flex flex-col gap-1">
    {meta.legend.map(entry => (
      <span
        key={`${entry.palette_index}-${entry.species_code}`}
        className="flex items-center gap-1.5 text-[11px] font-roboto text-neutral-300"
      >
        <span
          className="inline-block w-3 h-3 rounded-sm flex-shrink-0"
          style={{ backgroundColor: entry.color }}
        />
        <span className="truncate">{entry.species_name}</span>
      </span>
    ))}
  </div>
);

const RichnessLegendBody: React.FC<{ meta: SpeciesRichnessOverlayMeta }> = ({ meta }) => {
  const [vmin, vmax] = meta.valueRange;
  return (
    <>
      <div
        className="h-3 w-full rounded-sm"
        style={{ background: VIRIDIS_CSS_GRADIENT }}
      />
      <div className="flex items-center justify-between text-[10px] font-roboto-mono text-neutral-400 mt-1 w-[180px]">
        <span>{vmin} sp.</span>
        <span>{vmax} sp.</span>
      </div>
    </>
  );
};

/**
 * Floating legend for the species-diversity map overlay. When viewing the
 * dominant-species raster, we show a per-shed categorical legend built from
 * the meta sidecar (each shed has its own top-species list). For the richness
 * raster we show a viridis gradient keyed on the per-shed valueRange.
 *
 * Positioning is handled by the parent container in DetailMapView, which
 * stacks this legend below the variant toggle.
 *
 * The renderer is chosen from the meta shape itself (presence of `legend` vs
 * `valueRange`) rather than a separate variant prop, so that during a variant
 * switch — when the caller's variant has updated but the refetched meta has
 * not — we still render the legend that matches the meta actually in hand.
 *
 * The "Per 30 m pixel" wording on the richness title distinguishes this from
 * the shed-level Richness stat card, which counts species above the 0.5%
 * biomass threshold across the whole shed — a different number.
 */
export const SpeciesMapLegend: React.FC<SpeciesMapLegendProps> = ({ meta }) => {
  const isDominant = 'legend' in meta;
  return (
    <LegendCard
      title={isDominant ? 'Dominant species' : 'Species per 30 m pixel'}
      className="min-w-[200px] max-w-[260px]"
    >
      {isDominant ? (
        <DominantLegendBody meta={meta} />
      ) : (
        <RichnessLegendBody meta={meta} />
      )}
    </LegendCard>
  );
};
